import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitAction } from '../../redux/gameSlice';
import './ActionPanel.css';

const TURN_SECONDS = 30;

// Display money compactly: whole numbers as-is, fractional blinds to 2 decimals.
const fmt = (n) => {
    const num = Number(n);
    if (isNaN(num)) return '0';
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

// minRaise/maxRaise are the legal raise-TO bounds, computed by GameTable from the full
// game state (see minRaiseTo in utilities/chips). They match the backend's accepted
// range exactly: [currentBetLevel + max(bigBlind, lastRaiseSize), myCurrentBet + myChips].
// noBetRaise: a resolved Skip Power-Up blocks this seat from betting/raising this stage —
// the raise controls grey out (check/call/fold stay live); the backend enforces it anyway.
// noFold: a resolved Sacrifice Power-Up blocks this seat from FOLDING this stage — the
// fold button greys out (check/call/raise stay live); the backend enforces it anyway.
// blindSurcharge: a Flying Blind is contesting the round and this seat is NOT blind — every
// bet/call/raise pays this many extra chips (2 big blinds) into the blind side pot. The
// backend applies it either way; this only warns the player before they act.
// ghostCall: a resolved Ghost Power-Up makes this seat's next CALL free — the call button
// reads "(free)" with no amount owed; the backend's call paths consume the flag anyway.
const ActionPanel = ({ roomId, callAmount, minRaise, maxRaise, bigBlind, pot, currentBetLevel, stage, turnStartTime, noBetRaise, noFold, ghostCall = false, blindSurcharge = 0 }) => {
    const dispatch = useDispatch();
    // Player-customized quick-select presets (Settings → Rates Slider). preflop values are
    // big-blind multiples; postflop values are % of pot (converted to a fraction below).
    const betSettings = useSelector(state => state.playerInfo.betSettings);
    const [raiseAmount, setRaiseAmount] = useState(minRaise);
    const [secondsLeft, setSecondsLeft] = useState(TURN_SECONDS);
    const [showFoldConfirm, setShowFoldConfirm] = useState(false);

    const clampRaise = (v) => Math.min(maxRaise, Math.max(minRaise, v));

    // Pot-percentage sizing kicks in after preflop (flop/turn/river); preflop keeps the
    // big-blind multiple presets.
    const usePotPresets = stage === 'flop' || stage === 'turn' || stage === 'river';

    // Bet amounts are whole chips on integer-blind tables, cents on micro-stakes.
    const roundChip = (v) => Number.isInteger(Number(bigBlind)) ? Math.round(v) : Math.round(v * 100) / 100;

    // Keep the raise amount valid as the bet bounds change between turns/rounds.
    useEffect(() => {
        setRaiseAmount(prev => {
            const n = Number(prev);
            if (prev === '' || isNaN(n)) return minRaise;
            return Math.min(maxRaise, Math.max(minRaise, n));
        });
    }, [minRaise, maxRaise]);

    // Countdown timer
    useEffect(() => {
        if (!turnStartTime) return;
        const update = () => {
            const elapsed = Math.floor((Date.now() - turnStartTime) / 1000);
            setSecondsLeft(Math.max(0, TURN_SECONDS - elapsed));
        };
        update();
        const interval = setInterval(update, 500);
        return () => clearInterval(interval);
    }, [turnStartTime]);

    const handleAction = (actionType, amount = 0) => {
        dispatch(submitAction({ roomId, actionType, amount }));
    };

    // When the player can check (no chips owed), folding throws away a free chance to
    // stay in the hand — confirm first. Otherwise fold immediately.
    const handleFoldClick = () => {
        if (callAmount === 0) {
            setShowFoldConfirm(true);
        } else {
            handleAction('fold');
        }
    };

    const confirmFold = () => {
        setShowFoldConfirm(false);
        handleAction('fold');
    };

    const setPreset = (mult) => {
        setRaiseAmount(clampRaise(parseFloat((mult * bigBlind).toFixed(2))));
    };

    // Pot-percentage sizing (postflop). `pot` already includes every live bet, so the
    // chips that would be on the table if you only called is (pot + callAmount) — that's
    // the "100% pot raise add". A P% selection adds P× that on top of the bet you're
    // matching (currentBetLevel), giving the raise-TO shown in the box / in front of you.
    // For an opening bet callAmount and currentBetLevel are both 0, so this reduces to
    // P% of the pot. clampRaise applies the legal floor automatically: ≥ 1 BB for a bet,
    // ≥ the previous raise-add when re-raising.
    const setPotPreset = (pct) => {
        const potAfterCall = (Number(pot) || 0) + (Number(callAmount) || 0);
        const raiseAdd = roundChip(potAfterCall * pct);
        setRaiseAmount(clampRaise(raiseAdd + (Number(currentBetLevel) || 0)));
    };

    const raiseInvalid = raiseAmount === '' || raiseAmount < minRaise || raiseAmount > maxRaise;
    const timerColor = secondsLeft <= 10 ? '#ff3d6e' : secondsLeft <= 20 ? '#ffd75e' : '#97a2d8';

    return (
        <div className="action-panel">
            {/* Skip Power-Up notice: betting/raising is blocked for this stage. */}
            {noBetRaise && (
                <div className="skip-block-hint">Skipped — you cannot bet or raise this stage</div>
            )}
            {/* Sacrifice Power-Up notice: folding is blocked for this stage. */}
            {noFold && (
                <div className="skip-block-hint">Sacrificed — you cannot fold this stage</div>
            )}
            {/* Flying Blind notice: this (non-blind) seat pays the blind-pot surcharge on
                every chip action while a blind player is still in the round. */}
            {blindSurcharge > 0 && (
                <div className="skip-block-hint">
                    Flying Blind — betting, calling, or raising adds {fmt(blindSurcharge)} to the blind pot
                </div>
            )}

            {/* Top row: raise sizing */}
            <div className="raise-row">
                <div className="bet-presets">
                    {usePotPresets
                        ? betSettings.postflop.map((p, i) => (
                            <button key={i} className="preset-btn" onClick={() => setPotPreset(p / 100)}>
                                {p}%
                            </button>
                        ))
                        : betSettings.preflop.map((m, i) => (
                            <button key={i} className="preset-btn" onClick={() => setPreset(m)}>
                                {m}BB
                            </button>
                        ))}
                </div>
                <div className="raise-amount-box">
                    <span className="amount-chip">♠</span>
                    <input
                        type="number"
                        className="raise-input"
                        min={minRaise}
                        max={maxRaise}
                        step={bigBlind}
                        value={raiseAmount}
                        onChange={e => setRaiseAmount(e.target.value === '' ? '' : parseFloat(e.target.value))}
                        onBlur={e => {
                            const v = parseFloat(e.target.value);
                            setRaiseAmount(isNaN(v) ? minRaise : clampRaise(v));
                        }}
                    />
                </div>
                <input
                    type="range"
                    className="raise-slider"
                    min={minRaise}
                    max={maxRaise}
                    step={bigBlind}
                    value={raiseAmount === '' ? minRaise : raiseAmount}
                    onChange={e => setRaiseAmount(parseFloat(e.target.value))}
                />
            </div>

            {/* Bottom row: actions */}
            <div className="action-row">
                <div className="time-bank">
                    <span className="time-bank-label">Time Bank</span>
                    <span className="time-bank-value" style={{ color: timerColor }}>{secondsLeft}s</span>
                </div>
                <button
                    className="action-btn fold-btn"
                    disabled={!!noFold}
                    title={noFold ? 'A Sacrifice card blocks you from folding this stage' : undefined}
                    onClick={handleFoldClick}
                >
                    <span className="btn-icon icon-fold">✕</span> Fold
                </button>
                {callAmount === 0 ? (
                    <button className="action-btn check-btn" onClick={() => handleAction('check')}>
                        <span className="btn-icon icon-check">✔</span> Check
                    </button>
                ) : (
                    <button
                        className="action-btn call-btn"
                        onClick={() => handleAction('call')}
                        title={ghostCall ? 'Your Ghost makes this call free — no chips move' : undefined}
                    >
                        <span className="btn-icon icon-check">✔</span> Call{' '}
                        <span className="btn-amount">{ghostCall ? '(free)' : fmt(callAmount)}</span>
                    </button>
                )}
                <button
                    className="action-btn raise-btn"
                    disabled={raiseInvalid || noBetRaise}
                    title={noBetRaise ? 'A Skip card blocks you from betting or raising this stage' : undefined}
                    onClick={() => handleAction('raise', raiseAmount)}
                >
                    <span className="btn-icon icon-raise">▲</span> Raise <span className="btn-amount">{fmt(raiseAmount)}</span>
                </button>
            </div>

            {showFoldConfirm && (
                <div className="fold-confirm-overlay" onClick={() => setShowFoldConfirm(false)}>
                    <div className="fold-confirm-dialog" onClick={e => e.stopPropagation()}>
                        <p className="fold-confirm-text">
                            Are you sure you want to fold? You have the option to check.
                        </p>
                        <div className="fold-confirm-buttons">
                            <button className="action-btn fold-btn" onClick={confirmFold}>
                                <span className="btn-icon icon-fold">✕</span> Fold
                            </button>
                            <button className="action-btn" onClick={() => setShowFoldConfirm(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActionPanel;
